"use client";

import { ChevronDown, ChevronUp } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

const JourneyTimeline: React.FC<{
  journeyImages?: ({ url: string; width?: number; height?: number } | null)[];
}> = ({ journeyImages }) => {
  const [showAll, setShowAll] = useState(false);
  const timelineData = [
    {
      year: "2019",
      title: "Greening the Barren Lands of Kanha",
      description:
        "Our journey began by transforming 1,400 barren acres in Telangana into a lush, thriving ecological sanctuary. This pioneering restoration project was honored with the 'Pride of Telangana' award and earned the Indian Green Building Council's prestigious Platinum rating, establishing a world-class benchmark for all our future greening and restoration initiatives.",
      position: "left",
      image: journeyImages?.[0],
    },
    {
      year: "2021",
      title: "Launching the Forests by Heartfulness Movement",
      description:
        "To scale our environmental impact, we officially launched the Forests by Heartfulness movement. We set a bold national goal to plant thirty million native trees, focusing on conserving rare, endangered, and threatened species. To grow these resilient saplings, we began building a robust, nationwide network of eighteen active local nurseries.",
      position: "right",
      image: journeyImages?.[1],
    },
    {
      year: "2022",
      title: "Scaling Through Corporate Partnerships",
      description:
        "Our ecological mission quickly gained massive momentum as we established vital partnerships with key corporate donors. We secured large-scale commitments to plant millions of trees across India. To meet this demand, our central Hyderabad nursery expanded to fifty-four acres, ensuring high-quality saplings capable of surviving in hostile local climate conditions.",
      position: "left",
      image: journeyImages?.[2],
    },
    {
      year: "2023",
      title: "Large-Scale Eco-Restoration and Community Runs",
      description:
        "We undertook massive ecological challenges, initiating a multi-year project to restore over two thousand barren acres of degraded rocky land in Shivgarh, Madhya Pradesh. To build grassroots awareness, we launched the 'Green Run' series, directly channeling participant entry proceeds into our native and endangered tree afforestation campaigns across the country.",
      position: "right",
      image: journeyImages?.[3],
    },
    {
      year: "2024",
      title: "Technological Innovation and Massive Mobilization",
      description:
        "This year marked a breakthrough in scientific afforestation and mass community mobilization. We implemented state-of-the-art technologies, including the world's largest deployment of Rain Guns at Taranga Hills, Gujarat. Simultaneously, we launched the 'Ek Ped Maa Ke Naam' initiative, engaging ten thousand villagers in a single day to plant native trees.",
      position: "left",
      image: journeyImages?.[4],
    },
    {
      year: "2025",
      title: "Expanding Nurseries and Empowering Eco-Warriors",
      description:
        "By 2025, our infrastructure reached unprecedented heights with twenty-eight active nurseries nationwide raising millions of saplings. To ensure long-term sustainability, we trained thousands of forestry personnel, local farmers, and forest guards in advanced ecological techniques like soil enrichment, biochar preparation, and tree translocation, successfully offsetting thousands of carbon dioxide tons.",
      position: "right",
      image: journeyImages?.[5],
    },
    {
      year: "2026 & Beyond",
      title: "Rejuvenating Historic Water Ecosystems",
      description:
        "Looking forward, we are pioneering a massive eco-restoration project across the historic Braj Region. By adopting ten ancient sacred Kunds in Govardhan, Gokul, Barsana, Vrindavan, and Mathura, we are combining wetland restoration with dense tree plantations and herbal gardens, transforming these cultural sites into thriving hubs of local plant biodiversity.",
      position: "left",
      image: journeyImages?.[6],
    },
  ];

  return (
    <div className="w-full text-center bg-white md:px-8 px-4">
      <h1 className="text-[22px] sm:text-[32px] font-[Playfair_Display] font-semibold text-black md:text-[32px] md:font-semibold md:leading-[48px] md:align-middle md:text-[#090C0F] md:mb-8 mb-6 max-sm:text-start">
        Our Journey
      </h1>

      <div className="relative">
        {/* Center line */}
        <div className="absolute left-3 md:left-1/2 transform md:-translate-x-1/2 h-full md:w-1 w-[3px] bg-[#9DE1C2] rounded-full"></div>

        {/* Timeline items */}
        <div className="flex flex-col max-md:gap-6">
          {timelineData.map((item, index) => (
            <div
              key={index}
              className={`relative ${
                index >= 2 && !showAll ? "max-md:hidden -mt-20" : ""
              } ${index >= 1 && "md:-mt-12"}`}
            >
              {/* Desktop layout */}
              <div className="">
                {item.position === "left" ? (
                  <div className="flex items-center">
                    {/* Left content */}
                    <div className="md:w-1/2 max-md:pl-10 md:pr-8 max-md:text-left md:text-right">
                      <div className="inline-block">
                        {item.image && item.image.url ? (
                          <Image
                            src={item.image.url}
                            alt={item.title}
                            width={item.image.width ?? 297}
                            height={item.image.height ?? 144}
                            className="sm:w-[297px] sm:max-h-[144px] w-[198px] max-h-[96px] rounded-[5.38px] sm:rounded-[8.7px] object-cover md:mb-6 mb-4 md:ml-auto"
                          />
                        ) : (
                          <Image
                            src="/images/journey.png"
                            alt={item.title}
                            width={296}
                            height={144}
                            className="sm:w-[297px] sm:max-h-[144px] w-[198px] max-h-[96px] rounded-[5.38px] sm:rounded-[8.7px] object-cover md:mb-6 mb-4 md:ml-auto"
                          />
                        )}
                        <h3 className="sm:text-xl leading-6 font-bold md:mb-4 mb-2">
                          {item.year} — {item.title}
                        </h3>
                        <p className="md:text-lg text-sm font-medium text-[#454950] md:leading-6">
                          {item.description}
                        </p>
                      </div>
                    </div>

                    {/* Center dot */}
                    <div className="absolute flex items-center justify-center md:top-[60%] md:left-1/2 left-1 transform md:-translate-x-1/2 md:w-8 md:h-8 w-[18px] h-[18px] border-[#9DE1C2] rounded-full border-[0.4px] bg-[#E7F8F0] z-10">
                      <div className="bg-[#007A3F] rounded-full md:h-[18.5px] md:w-[18.5px] h-[10.5px] w-[10.5px]"></div>
                    </div>

                    {/* Right empty space */}
                    <div className="md:w-1/2 md:pl-8"></div>
                  </div>
                ) : (
                  <div className="flex items-center">
                    {/* Left empty space */}
                    <div className="md:w-1/2 md:pr-8"></div>

                    {/* Center dot */}
                    <div className="absolute flex items-center justify-center md:top-[60%] md:left-1/2 left-1 transform md:-translate-x-1/2 md:w-8 md:h-8 w-[18px] h-[18px] border-[#9DE1C2] rounded-full border-[0.4px] bg-[#E7F8F0] z-10">
                      <div className="bg-[#007A3F] rounded-full md:h-[18.5px] md:w-[18.5px] h-[10.5px] w-[10.5px]"></div>
                    </div>

                    {/* Right content */}
                    <div className="md:w-1/2 md:pl-8 pl-10">
                      <div className="inline-block text-left">
                        {item.image && item.image.url ? (
                          <Image
                            src={item.image.url}
                            alt={item.title}
                            width={item.image.width ?? 297}
                            height={item.image.height ?? 144}
                            className="sm:w-[297px] sm:max-h-[144px] w-[198px] max-h-[96px] rounded-[5.38px] sm:rounded-[8.7px] object-cover md:mb-6 mb-4"
                          />
                        ) : (
                          <Image
                            src="/images/journey.png"
                            alt={item.title}
                            width={296}
                            height={144}
                            className="sm:w-[297px] sm:max-h-[144px] w-[198px] max-h-[96px] rounded-[5.38px] sm:rounded-[8.7px] object-cover md:mb-6 mb-4"
                          />
                        )}
                        <h3 className="sm:text-xl leading-6 font-bold md:mb-4 mb-2">
                          {item.year} — {item.title}
                        </h3>
                        <p className="md:text-lg text-sm font-medium text-[#454950] md:leading-6">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden flex justify-center mt-4">
        {!showAll ? (
          <button
            onClick={() => setShowAll(true)}
            className="leading-4.5 h-[42px] px-3 py-[11px] text-[#003399] font-bold text-sm text:bg-[#00339990] transition-colors flex items-center gap-2 uppercase"
          >
            View More
            <ChevronDown />
          </button>
        ) : (
          <button
            onClick={() => setShowAll(false)}
            className="leading-4.5 h-[42px] px-3 py-[11px] text-[#003399] font-bold text-xs text:bg-[#00339990] transition-colors flex items-center gap-2 uppercase"
          >
            View Less
            <ChevronUp />
          </button>
        )}
      </div>
    </div>
  );
};

export default JourneyTimeline;
